import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
} from '@you/ui'
import { useEffect, useState, useMemo, useRef } from 'react'
import OptionCard from './OptionCard'
import type { SaveDatasetConfigRequest } from '@/api/types/datasetFiles.type'

// 选项接口
interface Strategy {
  name: string
  value: number // 0 或 1
  icon: string
  describe: string
}

// 分段标识选项
const segmentIdentifiers = [
  { value: '\n', label: '换行' },
  { value: '\n\n', label: '2个换行' },
  { value: '。', label: '中文句号' },
  { value: '！', label: '中文叹号' },
  { value: '.', label: '英文句号' },
  { value: '!', label: '英文叹号' },
  { value: '？', label: '中文问号' },
  { value: '?', label: '英文问号' },
]

// 文本预处理规则选项
const textPreprocessRules = [
  {
    value: 'replace_whitespace',
    label: '替换掉连续的空格、换行符和制表符删除',
  },
  { value: 'remove_urls_emails', label: '所有 URL 和电子邮箱地址' },
]

// 提取内容选项
const extractContentOptions = [
  { value: 1, label: '图片元素' },
  { value: 2, label: '扫描件(OCR)' },
  { value: 3, label: '表格元素' },
]

// 组件属性接口
interface DatasetSettingTextProps {
  /** 初始配置，用于回填数据 */
  initialConfig?: SaveDatasetConfigRequest
  /** 配置变化回调 */
  onConfigChange: (config: SaveDatasetConfigRequest) => void
}

const DatasetSettingText = ({
  initialConfig,
  onConfigChange,
}: DatasetSettingTextProps) => {
  // 内部状态管理
  const [selectedParseStrategy, setSelectedParseStrategy] = useState<number>(0)
  const [selectedSegmentStrategy, setSelectedSegmentStrategy] =
    useState<number>(0)
  const [segmentIdentifier, setSegmentIdentifier] = useState('\n')
  const [maxSegmentLength, setMaxSegmentLength] = useState('0')
  const [segmentOverlapRate, setSegmentOverlapRate] = useState('0')
  const [selectedPreprocessRules, setSelectedPreprocessRules] = useState<
    string[]
  >([])
  const [selectedExtractContent, setSelectedExtractContent] = useState<
    string[]
  >([])
  const [contentFilter, setContentFilter] = useState('')

  // 用于跟踪是否是初始化状态
  const isInitializing = useRef(true)
  const hasInitialized = useRef(false)
  const lastInitialConfig = useRef<SaveDatasetConfigRequest | undefined>(
    undefined
  )
  const skipNextConfigChange = useRef(false)

  // 解析策略选项（0: 快速解析, 1: 精准解析）
  const parseStrategies: Strategy[] = [
    {
      name: '快速解析',
      value: 0,
      icon: 'rocket',
      describe: '不会对文档提取图像、表格等元素，适用于纯文本',
    },
    {
      name: '精准解析',
      value: 1,
      icon: 'target',
      describe: '将从文档中提取图片、表格等元素，需要耗费更长的时间',
    },
  ]

  // 分段策略选项（0: 自动分段与清洗, 1: 自定义）
  const segmentStrategies: Strategy[] = [
    {
      name: '自动分段与清洗',
      value: 0,
      icon: 'auto',
      describe: '自动分段与预处理规则',
    },
    {
      name: '自定义',
      value: 1,
      icon: 'customize',
      describe: '自定义分段规则、分段长度及预处理规则',
    },
  ]

  // 处理数字输入
  const handleNumberInput = (
    value: string,
    setter: (value: string) => void
  ) => {
    const numValue = value.replace(/[^0-9]/g, '')
    setter(numValue)
  }

  // 处理预处理规则选择
  const handlePreprocessRuleChange = (ruleValue: string) => {
    const newRules = selectedPreprocessRules.includes(ruleValue)
      ? selectedPreprocessRules.filter((rule) => rule !== ruleValue)
      : [...selectedPreprocessRules, ruleValue]
    setSelectedPreprocessRules(newRules)
  }

  // 处理提取内容选择
  const handleExtractContentChange = (contentValue: string) => {
    const newContent = selectedExtractContent.includes(contentValue)
      ? selectedExtractContent.filter((content) => content !== contentValue)
      : [...selectedExtractContent, contentValue]
    setSelectedExtractContent(newContent)
  }

  // 构建完整的配置对象
  const buildConfig = useMemo((): SaveDatasetConfigRequest => {
    return {
      analyticalStrategies: selectedParseStrategy,
      partitioningStrategies: selectedSegmentStrategy,
      configDetail: {
        analytical:
          selectedParseStrategy === 0
            ? null
            : {
                contentFilter: contentFilter,
                extractContentType: selectedExtractContent.map(Number),
              },
        partitioning:
          selectedSegmentStrategy === 0
            ? null
            : {
                segmentIdentifier: segmentIdentifier,
                maximumSegmentLength: parseInt(maxSegmentLength),
                segmentOverlapRate: parseInt(segmentOverlapRate),
                replaceContinuousSymbols:
                  selectedPreprocessRules.includes('replace_whitespace'),
                allUrlAndEmail:
                  selectedPreprocessRules.includes('remove_urls_emails'),
                retainSegmentIdentifier: true,
              },
      },
    }
  }, [
    selectedParseStrategy,
    selectedSegmentStrategy,
    contentFilter,
    selectedExtractContent,
    segmentIdentifier,
    maxSegmentLength,
    segmentOverlapRate,
    selectedPreprocessRules,
  ])

  // 应用初始配置
  useEffect(() => {
    if (!initialConfig) return

    // 检查initialConfig是否真的发生了变化
    const configChanged =
      JSON.stringify(initialConfig) !==
      JSON.stringify(lastInitialConfig.current || {})
    if (!configChanged && hasInitialized.current) return

    // 标记跳过下一次配置变化回调
    skipNextConfigChange.current = true

    // 策略设置
    if (typeof initialConfig.analyticalStrategies === 'number') {
      setSelectedParseStrategy(initialConfig.analyticalStrategies)
    }
    if (typeof initialConfig.partitioningStrategies === 'number') {
      setSelectedSegmentStrategy(initialConfig.partitioningStrategies)
    }

    const analytical = initialConfig.configDetail?.analytical
    const partitioning = initialConfig.configDetail?.partitioning

    // 精准解析详细配置
    if (analytical) {
      setContentFilter(analytical.contentFilter ?? '')
      const types = analytical.extractContentType ?? []
      setSelectedExtractContent(types.map(String))
    }

    // 自定义分段配置
    if (partitioning) {
      if (typeof partitioning.segmentIdentifier === 'string') {
        setSegmentIdentifier(partitioning.segmentIdentifier)
      }
      if (typeof partitioning.maximumSegmentLength === 'number') {
        setMaxSegmentLength(String(partitioning.maximumSegmentLength))
      }
      if (typeof partitioning.segmentOverlapRate === 'number') {
        setSegmentOverlapRate(String(partitioning.segmentOverlapRate))
      }
      const rules: string[] = []
      if (partitioning.replaceContinuousSymbols)
        rules.push('replace_whitespace')
      if (partitioning.allUrlAndEmail) rules.push('remove_urls_emails')
      setSelectedPreprocessRules(rules)
    }

    // 更新引用
    lastInitialConfig.current = initialConfig
    hasInitialized.current = true
    isInitializing.current = false
  }, [initialConfig])

  // 当配置变化时，通知父组件（仅在非初始化状态下）
  useEffect(() => {
    if (isInitializing.current) return
    if (skipNextConfigChange.current) {
      skipNextConfigChange.current = false
      return
    }
    onConfigChange(buildConfig)
  }, [buildConfig, onConfigChange])

  return (
    <div className="space-y-8">
      {/* 文档解析策略 */}
      <div>
        <h3 className="text-base font-bold text-[#333333] mb-4">
          文档解析策略
        </h3>
        <div className="flex space-x-6">
          {parseStrategies.map((strategy) => (
            <OptionCard
              key={strategy.name}
              name={strategy.name}
              icon={strategy.icon}
              describe={strategy.describe}
              selected={selectedParseStrategy === strategy.value}
              onClick={() => setSelectedParseStrategy(strategy.value)}
            />
          ))}
        </div>

        {/* 精准解析详细配置 */}
        {selectedParseStrategy === 1 && (
          <div
            className="mt-6 p-4 rounded-lg"
            style={{ background: 'rgba(78, 87, 254, 0.03)' }}
          >
            <div className="space-y-4">
              {/* 提取内容 */}
              <div>
                <label className="block text-sm font-medium text-[#1D2129] mb-2">
                  提取内容
                </label>
                <div className="flex space-x-4">
                  {extractContentOptions.map((option) => (
                    <Checkbox
                      key={option.value}
                      checked={selectedExtractContent.includes(
                        option.value.toString()
                      )}
                      onChange={(checked) => {
                        if (checked) {
                          handleExtractContentChange(option.value.toString())
                        } else {
                          handleExtractContentChange(option.value.toString())
                        }
                      }}
                      label={option.label}
                      size="md"
                    />
                  ))}
                </div>
              </div>

              {/* 内容过滤 */}
              <div>
                <label className="block text-sm font-medium text-[#1D2129] mb-2">
                  内容过滤
                </label>
                <Input
                  value={contentFilter}
                  onChange={(e) => setContentFilter(e.target.value)}
                  placeholder="请输入过滤条件"
                  className="text-[#1D2129] text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 分段策略 */}
      <div>
        <h3 className="text-base font-bold text-[#333333] mb-4">分段策略</h3>
        <div className="flex space-x-6">
          {segmentStrategies.map((strategy) => (
            <OptionCard
              key={strategy.name}
              name={strategy.name}
              icon={strategy.icon}
              describe={strategy.describe}
              selected={selectedSegmentStrategy === strategy.value}
              onClick={() => setSelectedSegmentStrategy(strategy.value)}
            />
          ))}
        </div>

        {/* 自定义设置面板 */}
        {selectedSegmentStrategy === 1 && (
          <div
            className="mt-6 p-4 rounded-lg"
            style={{ background: 'rgba(78, 87, 254, 0.03)' }}
          >
            <div className="space-y-4">
              {/* 分段标识 */}
              <div>
                <label className="block text-sm font-medium text-[#1D2129] mb-2">
                  分段标识 <span className="text-red-500">*</span>
                </label>
                <Select
                  value={segmentIdentifier}
                  onValueChange={setSegmentIdentifier}
                >
                  <SelectTrigger className="w-full text-[#1D2129] text-sm bg-white">
                    <SelectValue placeholder="请选择分段标识" />
                  </SelectTrigger>
                  <SelectContent>
                    {segmentIdentifiers.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 分段最大长度 */}
              <div>
                <label className="block text-sm font-medium text-[#1D2129] mb-2">
                  分段最大长度 <span className="text-red-500">*</span>
                </label>
                <Input
                  value={maxSegmentLength}
                  onChange={(e) =>
                    handleNumberInput(e.target.value, setMaxSegmentLength)
                  }
                  placeholder="请输入正整数"
                  className="text-[#1D2129] text-sm"
                />
              </div>

              {/* 分段重复率 */}
              <div>
                <label className="block text-sm font-medium text-[#1D2129] mb-2">
                  分段重复率% <span className="text-red-500">*</span>
                </label>
                <Input
                  value={segmentOverlapRate}
                  onChange={(e) =>
                    handleNumberInput(e.target.value, setSegmentOverlapRate)
                  }
                  placeholder="请输入正整数"
                  className="text-[#1D2129] text-sm"
                />
              </div>

              {/* 文本预处理规则 */}
              <div>
                <label className="block text-sm font-medium text-[#1D2129] mb-2">
                  文本预处理规则
                </label>
                <div className="space-y-2 flex flex-col">
                  {textPreprocessRules.map((rule) => (
                    <Checkbox
                      key={rule.value}
                      checked={selectedPreprocessRules.includes(rule.value)}
                      onChange={(checked) => {
                        if (checked) {
                          handlePreprocessRuleChange(rule.value)
                        } else {
                          handlePreprocessRuleChange(rule.value)
                        }
                      }}
                      label={rule.label}
                      size="md"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DatasetSettingText
